import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";

// React context for the active tenant + the user's full tenant membership.
//
// Reads tenant claims injected into the JWT by the Supabase custom access
// token hook (Phase 2.2): tenant_id (active), tenants[] (all memberships),
// is_admin (platform admin). Fetches the corresponding rows from the
// `tenants` table so consumers can read tenant.branding, tenant.plan, etc.
//
// Nothing in the existing codebase uses this yet — Phase 4.2 migrates
// COMPANIES-dict consumers to use it. Adding the provider here is
// strictly additive.

const TenantContext = createContext(null);

function decodeJwtClaims(accessToken) {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

export function TenantProvider({ children }) {
  const [activeTenant, setActiveTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setActiveTenant(null);
        setTenants([]);
        setIsAdmin(false);
        return;
      }

      const claims = decodeJwtClaims(session.access_token);
      const claimIsAdmin = Boolean(claims?.is_admin);
      setIsAdmin(claimIsAdmin);

      const tenantIds = Array.isArray(claims?.tenants) ? claims.tenants : [];
      const activeId = claims?.tenant_id || tenantIds[0] || null;

      // Platform admins see every tenant (for support / oversight). Non-admins
      // see only the tenants they belong to via tenant_users. RLS enforces
      // this server-side; this client-side branching just decides what to
      // ask for.
      let query = supabase.from("tenants").select("*").order("name", { ascending: true });
      if (!claimIsAdmin) {
        if (tenantIds.length === 0) {
          setTenants([]);
          setActiveTenant(null);
          return;
        }
        query = query.in("id", tenantIds);
      }
      const { data: rows, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;

      setTenants(rows || []);
      setActiveTenant(rows?.find(t => t.id === activeId) || rows?.[0] || null);
    } catch (e) {
      setError(e);
      setActiveTenant(null);
      setTenants([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      load();
    });
    return () => subscription?.unsubscribe();
  }, [load]);

  // Switch which tenant is the user's currently-active one. Updates
  // tenant_users.is_default and forces a JWT refresh so the new claim
  // is picked up by RLS on subsequent queries.
  const switchTenant = useCallback(async (newTenantId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) throw new Error("Not signed in");
    const userId = session.user.id;

    // Clear the default flag on all of the user's rows, then set it on the
    // requested tenant. We .select() the second update so we can detect the
    // case where it matched 0 rows — which happens when the user has no
    // membership row for this tenant (e.g. an orphaned tenant created
    // without the auto-add, or a platform admin "viewing as" a tenant they
    // don't belong to). In that case the access-token hook would find no
    // is_default row and silently fall back to another tenant.
    await supabase.from("tenant_users").update({ is_default: false }).eq("user_id", userId);
    const { data: updated, error: setErr } = await supabase
      .from("tenant_users")
      .update({ is_default: true })
      .eq("user_id", userId)
      .eq("tenant_id", newTenantId)
      .select();
    if (setErr) throw setErr;

    if (!updated || updated.length === 0) {
      // No membership row. Self-heal by creating one as admin, then mark it
      // default. RLS allows this for platform admins (tenant_users_admin_insert)
      // and for the user acting on their own row. If the insert is rejected
      // (non-admin with no membership), surface a clear error instead of
      // silently leaving the user on the wrong tenant.
      const { error: insErr } = await supabase
        .from("tenant_users")
        .insert({ user_id: userId, tenant_id: newTenantId, role: "admin", is_default: true });
      if (insErr) {
        throw new Error(
          "You don't have access to that workspace and it couldn't be granted automatically: " +
          (insErr.message || String(insErr))
        );
      }
    }

    // Force a new JWT so the access token hook re-reads the default tenant
    // assignment and produces fresh claims (RLS + the app both key off the
    // tenant_id claim).
    await supabase.auth.refreshSession();
    await load();
  }, [load]);

  return (
    <TenantContext.Provider
      value={{
        tenant: activeTenant,
        tenants,
        isAdmin,
        isLoading,
        error,
        switchTenant,
        refresh: load,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (ctx === null) {
    throw new Error("useTenant must be used inside a <TenantProvider>");
  }
  return ctx;
}
